/* FedEx / Insite utilities – standalone (no ES modules). Exposes window.FedExUtils */

(function (global) {
  'use strict';

  var spritePath = '/files/theme/site-files/20261014/icon-sprite.svg';
  var iconSprite = null;

  /**
   * Resolves the sprite URL using the asset base (CDN) from companyVars when present.
   * @returns {string} Absolute URL for the sprite.
   */
  function getSpriteURL() {
    try {
      var el = global.document && global.document.getElementById('companyVars');
      if (el && el.textContent) {
        var vars = JSON.parse(el.textContent);
        if (vars && typeof vars.asset_url === 'string' && vars.asset_url) {
          var base = vars.asset_url.replace(/\/+$/, '');
          return base + spritePath;
        }
      }
    } catch (e) {}
    return (global.location && global.location.origin)
      ? global.location.origin + spritePath
      : spritePath;
  }

  /**
   * Loads the SVG sprite into the document body. Only loads once.
   * Uses the asset (CDN) base URL from companyVars so the sprite loads from the same origin as other assets.
   * @returns {Promise<void>}
   */
  function loadSVGSprite() {
    if (iconSprite) return Promise.resolve();
    var url = getSpriteURL();
    return fetch(url)
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'image/svg+xml');
        iconSprite = doc.querySelector('svg');
        if (iconSprite && global.document && global.document.body) {
          global.document.body.appendChild(iconSprite);
        }
      });
  }

  /**
   * Formats ® and ™ in text as superscript.
   * @param {string} text
   * @returns {string}
   */
  function formatWithSuperscript(text) {
    if (!text) return text;
    return text.replace(/[®™]/g, function (match) { return '<sup>' + match + '</sup>'; });
  }

  /**
   * Sanitizes a string for use as an HTML ID.
   * @param {string} str
   * @returns {string}
   */
  function sanitizeId(str) {
    if (!str) return '';
    var sanitized = str.toLowerCase()
      .replace(/[^a-z0-9-_]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return sanitized || '';
  }

  /**
   * Creates an SVG element with a use tag pointing to iconId in the sprite.
   * @param {string} iconId - id of the icon in the sprite
   * @param {Element} iconContainer - element to put the SVG in
   */
  function createSvgEl(iconId, iconContainer) {
    if (!iconContainer) return;
    iconContainer.innerHTML = '<svg><use /></svg>';
    var use = iconContainer.querySelector('use');
    if (use) use.setAttribute('href', '#' + iconId);
  }

  /**
   * Sets element id from its text content, adds anchored-content and tabindex -1.
   * Optionally replaces a child with the title element.
   * @param {Element} element - title element
   * @param {Element|null} replaceChildElement - optional parent to replace children with element
   */
  function setIdFromText(element, replaceChildElement) {
    if (!element) return;
    var text = element.textContent;
    var cleanText = sanitizeId(text);
    element.setAttribute('id', cleanText);
    element.classList.add('anchored-content');
    element.setAttribute('tabindex', '-1');
    element.innerHTML = formatWithSuperscript(text);
    if (replaceChildElement != null) {
      replaceChildElement.innerHTML = '';
      replaceChildElement.appendChild(element);
    }
  }

  /**
   * Removes title attribute from link if it equals the link text (a11y).
   * @param {Element} linkElement
   */
  function checkLinkTitle(linkElement) {
    if (!linkElement) return;
    var linkTitle = linkElement.getAttribute('title');
    var linkText = (linkElement.textContent || '').trim();
    if (linkTitle === linkText) linkElement.removeAttribute('title');
  }

  /**
   * Extracts locale (xx-xx) from URL path.
   * @param {string} [url] - defaults to window.location.href
   * @returns {string}
   */
  function getLocaleFromPath(url) {
    var targetUrl = url || (global.location && global.location.href) || '';
    try {
      var urlObj = new URL(targetUrl);
      var pathSegments = urlObj.pathname.split('/').filter(function (s) { return s.length > 0; });

      if (urlObj.hostname.indexOf('author-') !== -1 || urlObj.hostname.indexOf('publish-') !== -1) {
        var countrySegment = pathSegments[2];
        var languageSegment = pathSegments[3];
        if (/^[a-z]{2}$/i.test(countrySegment) && /^[a-z]{2}$/i.test(languageSegment)) {
          return countrySegment.toLowerCase() + '/' + languageSegment.toLowerCase();
        }
        return 'us/en';
      }

      if (pathSegments.length > 0 && /^[a-z]{2}-[a-z]{2}$/i.test(pathSegments[0])) {
        return pathSegments[0].toLowerCase();
      }
      return 'en-us';
    } catch (e) {
      return 'en-us';
    }
  }

  /**
   * Extracts language code from path (e.g. en-US).
   * @param {string} [url] - defaults to window.location.pathname
   * @returns {string}
   */
  function getLanguageFromUrl(url) {
    if (url == null && global.location) url = global.location.pathname;
    var match = (url || '').match(/\/([a-z]{2}-[a-z]{2})(?:\/|$)/i);
    if (match && match[1]) {
      var parts = match[1].toLowerCase().split('-');
      return parts[0] + '-' + (parts[1] ? parts[1].toUpperCase() : '');
    }
    return 'en-US';
  }

  /**
   * Sets multiple attributes on an element from an object.
   * @param {Element} element
   * @param {Object} attrs - key/value attributes
   */
  function setElementAttributes(element, attrs) {
    if (!element || !attrs) return;
    Object.keys(attrs).forEach(function (key) {
      element.setAttribute(key, attrs[key]);
    });
  }

  /**
   * Debounced function (invokes after wait ms after last call).
   * @param {Function} func
   * @param {number} [wait=300]
   * @returns {Function}
   */
  function debounce(func, wait) {
    var timeout;
    wait = wait == null ? 300 : wait;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () { func.apply(context, args); }, wait);
    };
  }

  var FedExUtils = {
    iconSpriteURL: spritePath,
    loadSVGSprite: loadSVGSprite,
    formatWithSuperscript: formatWithSuperscript,
    sanitizeId: sanitizeId,
    createSvgEl: createSvgEl,
    setIdFromText: setIdFromText,
    checkLinkTitle: checkLinkTitle,
    getLocaleFromPath: getLocaleFromPath,
    getLanguageFromUrl: getLanguageFromUrl,
    setElementAttributes: setElementAttributes,
    debounce: debounce
  };

  global.FedExUtils = FedExUtils;

})(typeof window !== 'undefined' ? window : this);
